import React from "react";
import Link from "@/components/Links/ActiveLink";
import SearchForm from "./SearchForm";
import useSWR from "swr";
import baseUrl from "@/utils/baseUrl";
import { signOut } from "next-auth/react";
import { UserRole } from "@/lib/shared/enums";
import { Avatar, Menu, Container, Group, Text, Flex, Badge, Box } from "@mantine/core";
import styles from "./Navbar.module.scss";
import DefaultButton from "../UI/DefaultButton";
import MenuItemLink from "../UI/MenuItemLink";
import {
  IconApple,
  IconBooks,
  IconCalendarClock,
  IconFiles,
  IconUserEdit,
  IconLayoutDashboard,
  IconSettings,
  IconLogout,
} from "@tabler/icons-react";
import Image from "next/image";
import { useRouter } from "next/router";

const fetcher = (...args) => fetch(...args).then((res) => res.json());

const Navbar = ({ user }) => {
  const router = useRouter();
  const { data } = useSWR(`${baseUrl}/api/v1/search-autocomplete`, fetcher);
  const courses = data ? data.courses : [];
  const teachers = data ? data.teachers : [];

  const isAdmin = user?.role === UserRole.ADMIN;
  const isTeacher = user?.role === UserRole.TEACHER;
  const isStudent = user?.role === UserRole.STUDENT;

  const stringAvatar = (name) => ({
    children: `${name.charAt(0)}${name.charAt(name.indexOf(" ") + 1 || 1)}`,
  });

  return (
    <Container id="navbar" maw={1220} className={styles.navbarContainer}>
      <Flex wrap="nowrap" justify="space-between" align="center">
        {/* Left Section: Logo & Search */}
        <Group wrap="nowrap" align="center" spacing="md">
          <Link legacyBehavior href="/" passHref>
            <a className="logo-icon">
              <Image
                width={140}
                height={70}
                src="/images/coursepage.svg"
                alt="logo"
                priority
                style={{ cursor: "pointer" }}
              />
            </a>
          </Link>
          {courses && teachers && <SearchForm courses={courses} teachers={teachers} />}
        </Group>

        {/* Right Section: User Dropdown or Login Button */}
        {user?.name ? (
          <Menu styles={{ dropdown: { borderRadius: "10px", padding: "0.4rem" } }} shadow="md" width={250}>
            <Menu.Target>
              <Avatar
                ml="md"
                className={styles.avatarBtn}
                size={40}
                src={user.profilePhoto}
                {...stringAvatar(user.name)}
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label mb={5}>
                <Group gap="sm" wrap="nowrap" align="center" spacing="sm">
                  <Avatar radius="xl" size={40} src={user.profilePhoto} />
                  <Box w={160}>
                    <Text truncate="end" c="black" fw={500} mb={0} size="sm">
                      {user.name}
                    </Text>
                    <Text truncate="end" size="xs" c="dimmed">
                      @{user.profileId}
                    </Text>
                  </Box>
                </Group>
              </Menu.Label>

              {isStudent &&
                (user.asTeacherApply ? (
                  <Menu.Label>
                    <Badge color="orange.5">Reviewing your application</Badge>
                  </Menu.Label>
                ) : (
                  <Menu.Item>
                    <MenuItemLink
                      href="/become-a-teacher"
                      icon={IconApple}
                      label="Apply as instructor"
                      textColor="#1976d2"
                    />
                  </Menu.Item>
                ))}

              <Menu.Item>
                <MenuItemLink href="/my-courses" icon={IconBooks} label="My Library" />
              </Menu.Item>

              <Menu.Item>
                <MenuItemLink href="/my-sessions" icon={IconCalendarClock} label="Calendar" />
              </Menu.Item>

              <Menu.Item>
                <MenuItemLink href="/user/my-account" icon={IconUserEdit} label="Account" />
              </Menu.Item>

              {(isTeacher || isAdmin) && (
                <Menu.Item>
                  <MenuItemLink href="/teacher/dashboard" icon={IconLayoutDashboard} label="Dashboard" />
                </Menu.Item>
              )}

              {isAdmin && (
                <Menu.Item>
                  <MenuItemLink href="/admin/pending-requests" icon={IconSettings} label="Admin Dashboard" />
                </Menu.Item>
              )}

              <Menu.Item>
                <MenuItemLink
                  external
                  icon={IconLogout}
                  label="Logout"
                  onClick={(e) => {
                    e.preventDefault();
                    signOut({ callbackUrl: `${baseUrl}/authentication` });
                  }}
                />
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <DefaultButton ml="md" mr={0} mb={0} text="Log In" onClick={() => router.push("/authentication")} />
        )}
      </Flex>
    </Container>
  );
};

export default Navbar;
